/* eslint-disable react/prop-types */
/* GlobalCopilotDrawer — workspace-wide AI assistant.

   Floats a small button at the bottom-right of every authenticated
   page. Click → opens a side drawer with a chat UI. The backend
   `/api/copilot/chat` enriches every prompt with workspace counts and
   recent items so the assistant can answer pipeline questions, draft
   emails, give feature guidance, etc. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, Send, X, Sparkles, Loader2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  "How many candidates are at interview stage?",
  "Draft a 'Submission' email to a fintech client",
  "Which candidates moved this week?",
  "Suggest 5 ATS keywords for a Senior Data Engineer JD",
  "Walk me through how to send an offer letter",
];

export default function GlobalCopilotDrawer() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: "Hi 👋  I'm your Mission Control Copilot.  Ask me about candidates, pipeline stages, open jobs, or how any feature in this app works.  I can also draft outreach emails and interview invites.",
      }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setInput("");
    const next = [...messages, { role: "user", text }];
    setMessages(next);
    setBusy(true);
    try {
      const r = await api.post("/copilot/chat", {
        message: text,
        history: next.map((m) => ({ role: m.role, content: m.text })),
      });
      setMessages([...next, { role: "assistant", text: r.data.reply || "(no reply)" }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copilot failed");
      setMessages([...next, { role: "assistant", text: "Sorry — I hit an error. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(true)}
        data-testid="global-copilot-launch"
        aria-label="Open AI Copilot"
        className="fixed bottom-5 right-5 z-30 size-12 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 grid place-items-center hover:scale-105 active:scale-95 transition-transform border-2 border-card"
      >
        <Bot className="size-5" />
        <span className="absolute -top-1 -right-1 size-3 bg-emerald-500 rounded-full border-2 border-card animate-pulse" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={() => setOpen(false)} />
          {/* Drawer */}
          <aside className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[420px] bg-card border-l border-border shadow-2xl flex flex-col" data-testid="global-copilot-drawer">
            <div className="h-14 px-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/10 to-transparent">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-md bg-primary text-primary-foreground grid place-items-center">
                  <Bot className="size-4" />
                </div>
                <div>
                  <div className="font-bold display text-sm leading-none">Mission Control Copilot</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">Workspace AI</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} data-testid="global-copilot-close"><X className="size-4" /></Button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`copilot-msg-${i}`}>
                  {m.role !== "user" && (
                    <div className="size-7 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0"><Bot className="size-3.5" /></div>
                  )}
                  <div className={`max-w-[78%] rounded-lg px-3 py-2 text-sm leading-snug whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                    {m.text}
                  </div>
                  {m.role === "user" && (
                    <div className="size-7 rounded-full bg-secondary text-foreground grid place-items-center shrink-0"><UserIcon className="size-3.5" /></div>
                  )}
                </div>
              ))}
              {busy && (
                <div className="flex gap-2 justify-start">
                  <div className="size-7 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0"><Bot className="size-3.5" /></div>
                  <div className="rounded-lg px-3 py-2 bg-secondary text-muted-foreground text-sm inline-flex items-center gap-2"><Loader2 className="size-3 animate-spin" /> Thinking…</div>
                </div>
              )}
            </div>

            {messages.length <= 1 && !busy && (
              <div className="px-4 pb-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 font-semibold flex items-center gap-1"><Sparkles className="size-3" /> Try</div>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_PROMPTS.map((p, i) => (
                    <button key={i} onClick={() => send(p)} className="text-[11px] px-2 py-1 rounded-full bg-secondary hover:bg-accent text-foreground border border-border" data-testid={`copilot-quick-${i}`}>{p}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-border p-3 flex items-end gap-2">
              <Textarea
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask anything…  (Enter to send, Shift+Enter for newline)"
                className="resize-none flex-1"
                disabled={busy}
                data-testid="global-copilot-input"
              />
              <Button onClick={() => send()} disabled={busy || !input.trim()} className="lift" data-testid="global-copilot-send">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              </Button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
